import { useState, useEffect } from 'react';

export default function FxRatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState({ from_currency: '', to_currency: '', rate_date: '' });
  const [form, setForm] = useState({ from_currency: '', to_currency: '', rate: '', rate_date: '', rate_type: 'spot' });

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.from_currency) params.set('from_currency', filter.from_currency);
      if (filter.to_currency) params.set('to_currency', filter.to_currency);
      if (filter.rate_date) params.set('rate_date', filter.rate_date);
      const r = await fetch(`/entities/fx-rates?${params}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load FX rates');
      setItems(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.from_currency || !form.to_currency || !form.rate || !form.rate_date) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/entities/fx-rates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_currency: form.from_currency.toUpperCase(),
          to_currency: form.to_currency.toUpperCase(),
          rate: parseFloat(form.rate),
          rate_date: form.rate_date,
          rate_type: form.rate_type,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to create FX rate');
      setForm({ from_currency: '', to_currency: '', rate: '', rate_date: '', rate_type: 'spot' });
      setShowForm(false);
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="entities-fx-rates-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>FX Rates</h1>
          <p className="muted">Foreign exchange rates used for consolidation and translation.</p>
        </div>
        <button className="btn primary" data-testid="fx-rate-add" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add Rate'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="entities-fx-rates-error">{error}</div>}

      <div className="card form-card row-form">
        <h3>Filter</h3>
        <div className="row-form">
          <label>From currency
            <input value={filter.from_currency} onChange={(e) => setFilter((f) => ({ ...f, from_currency: e.target.value }))} placeholder="e.g. USD" />
          </label>
          <label>To currency
            <input value={filter.to_currency} onChange={(e) => setFilter((f) => ({ ...f, to_currency: e.target.value }))} placeholder="e.g. EUR" />
          </label>
          <label>Rate date
            <input type="date" value={filter.rate_date} onChange={(e) => setFilter((f) => ({ ...f, rate_date: e.target.value }))} />
          </label>
          <div className="actions">
            <button className="btn ghost" onClick={() => setFilter({ from_currency: '', to_currency: '', rate_date: '' })}>Clear</button>
          </div>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="card form-card row-form">
          <label>From
            <input value={form.from_currency} onChange={(e) => setForm((f) => ({ ...f, from_currency: e.target.value }))} placeholder="USD" maxLength={3} />
          </label>
          <label>To
            <input value={form.to_currency} onChange={(e) => setForm((f) => ({ ...f, to_currency: e.target.value }))} placeholder="EUR" maxLength={3} />
          </label>
          <label>Rate
            <input type="number" step="0.000001" value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} placeholder="1.082300" />
          </label>
          <label>Date
            <input type="date" value={form.rate_date} onChange={(e) => setForm((f) => ({ ...f, rate_date: e.target.value }))} />
          </label>
          <label>Type
            <select value={form.rate_type} onChange={(e) => setForm((f) => ({ ...f, rate_type: e.target.value }))}>
              <option value="spot">Spot</option>
              <option value="average">Average</option>
              <option value="closing">Closing</option>
            </select>
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy || !form.from_currency || !form.to_currency || !form.rate || !form.rate_date}>Save</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="entity-fx-rate-list">
          <thead>
            <tr><th>From</th><th>To</th><th>Rate</th><th>Date</th><th>Type</th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} data-testid={`entity-fx-rate-row-${r.id}`}>
                <td>{r.from_currency}</td>
                <td>{r.to_currency}</td>
                <td>{Number(r.rate).toFixed(6)}</td>
                <td>{r.rate_date}</td>
                <td>{r.rate_type}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="muted">No FX rates found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
